import { useState, useCallback, useEffect, useRef } from 'react';

interface VersionInfo {
  version: string;
  released: string;
  builtAt?: string;
  notes?: string;
}

interface StoredUpdateInfo {
  latestVersion: string;
  hasUpdate: boolean;
  checkedAt: string;
}

interface UsePwaUpdateReturn {
  currentVersion: string;
  latestVersion: string | null;
  isChecking: boolean;
  hasUpdate: boolean;
  showUpdateBanner: boolean;
  checkForUpdate: () => Promise<void>;
  installUpdate: () => void;
  dismissBanner: () => void;
  error: string | null;
}

const STORAGE_KEY = 'lancarpos_update_info';
const UPDATE_BANNER_DISMISSED_KEY = 'lancarpos_update_banner_dismissed';

export function usePwaUpdate(): UsePwaUpdateReturn {
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const currentVersionRef = useRef<string>('');
  const checkingRef = useRef(false);
  const bannerDismissedRef = useRef(false);

  useEffect(() => {
    async function loadCurrentVersion() {
      try {
        const response = await fetch('/version.json', { 
          cache: 'no-store' 
        });
        if (response.ok) {
          const data: VersionInfo = await response.json();
          setCurrentVersion(data.version);
          currentVersionRef.current = data.version;
        }
      } catch (e) {
        console.warn('Could not load version:', e);
      }
    }

    function loadStoredUpdateInfo() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const bannerDismissed = localStorage.getItem(UPDATE_BANNER_DISMISSED_KEY);
        
        if (stored) {
          const info: StoredUpdateInfo = JSON.parse(stored);
          setLatestVersion(info.latestVersion);
          setHasUpdate(info.hasUpdate);
          
          bannerDismissedRef.current = bannerDismissed === info.latestVersion;
          
          if (info.hasUpdate && !bannerDismissedRef.current) {
            setShowUpdateBanner(true);
          }
        }
      } catch (e) {
        console.warn('Could not load stored update info:', e);
      }
    }

    async function checkForUpdates() {
      await loadCurrentVersion();
      loadStoredUpdateInfo();
      
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          window.location.reload();
        });
      }

      await doCheckForUpdate();
    }

    checkForUpdates();
  }, []);

  const doCheckForUpdate = useCallback(async () => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    
    setIsChecking(true);
    setError(null);

    try {
      const response = await fetch('/version.json', { 
        cache: 'no-store' 
      });
      if (!response.ok) {
        throw new Error('Gagal memuat informasi versi');
      }

      const data: VersionInfo = await response.json();
      setLatestVersion(data.version);
      currentVersionRef.current = data.version;
      
      if (!currentVersionRef.current) {
        setCurrentVersion(data.version);
        currentVersionRef.current = data.version;
      }

      const updateAvailable = Boolean(currentVersionRef.current && data.version > currentVersionRef.current);
      setHasUpdate(updateAvailable);

      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        latestVersion: data.version,
        hasUpdate: updateAvailable,
        checkedAt: new Date().toISOString()
      }));

      if (updateAvailable && !bannerDismissedRef.current) {
        setShowUpdateBanner(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memeriksa pembaruan');
    } finally {
      setIsChecking(false);
      checkingRef.current = false;
    }
  }, []);

  const checkForUpdate = useCallback(async () => {
    await doCheckForUpdate();
  }, [doCheckForUpdate]);

  const dismissBanner = useCallback(() => {
    setShowUpdateBanner(false);
    if (latestVersion) {
      localStorage.setItem(UPDATE_BANNER_DISMISSED_KEY, latestVersion);
      bannerDismissedRef.current = true;
    }
  }, [latestVersion]);

  const installUpdate = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration) {
          registration.update().then(() => {
            if (registration.waiting) {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            } else {
              console.log('No waiting service worker, will activate on next load');
            }
          }).catch((err) => {
            console.warn('Failed to update service worker:', err);
          });
        } else {
          console.warn('No service worker registration found');
        }
      }).catch((err) => {
        console.warn('Failed to get service worker registration:', err);
        setError('Gagal memperbarui. Silakan muat ulang halaman.');
      });
    }
  }, []);

  return {
    currentVersion,
    latestVersion,
    isChecking,
    hasUpdate,
    showUpdateBanner,
    checkForUpdate,
    installUpdate,
    dismissBanner,
    error,
  };
}
